/**
 * Admin: configurare modul Eveniment (imagini, texte, activ/inactiv).
 */
import { $ } from '../utils/helpers.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import { sanitizeImageSrc } from '../utils/url.js?v=83.21';
import { validateFileUpload } from '../utils/validation.js?v=83.21';
import { getCurrentUser } from '../services/auth.js?v=83.21';
import { getCurrentSettings, setCurrentSettings } from '../services/settings.js?v=83.21';
import { saveCampaignSettingsBackend } from '../services/backend.js?v=83.21';
import { uploadFile } from '../services/storage.js?v=83.21';
import { refreshAdminSummaries } from './admin.js?v=83.21';
import { logger } from '../utils/logger.js?v=83.21';

export const DEFAULT_EVENT_BUTTON_LABEL = 'Descoperă povestea noastră';
export const DEFAULT_EVENT_TITLE = 'Povestea noastră';

let localEventModuleImages = [];
let eventImagesHydrated = false;

/** Idei rapide — un tap completează titlul, butonul și textul. */
export const EVENT_PRESETS = [
  { id: 'live', label: '🎸 Muzică live', title: 'Seară cu muzică live', button: 'Vezi programul serii', text: 'Te așteptăm la o seară cu muzică live! Rezervă-ți masa din timp — locurile sunt limitate.' },
  { id: 'tasting', label: '🍷 Degustare', title: 'Seară de degustare', button: 'Detalii degustare', text: 'Descoperă gusturi noi alături de noi. Locuri limitate — anunță-ne dacă vii.' },
  { id: 'match', label: '⚽ Meci live', title: 'Meciul pe ecran mare', button: 'Vezi meciul la noi', text: 'Urmărește meciul la noi, pe ecran mare, cu prietenii. Vino din timp pentru cele mai bune locuri!' },
  { id: 'party', label: '🎉 Petrecere', title: 'Petrecere tematică', button: 'Vino la petrecere', text: 'Muzică, atmosferă bună și surprize pentru invitați. Te așteptăm!' },
  { id: 'launch', label: '🆕 Noutate', title: 'Noutate la noi', button: 'Vezi noutatea', text: 'Avem ceva nou pentru tine! Treci pe la noi să încerci.' }
];

export function applyEventPreset(presetId) {
  const p = EVENT_PRESETS.find((x) => x.id === presetId);
  if (!p) return;
  if ($('eventModuleTitle')) $('eventModuleTitle').value = p.title;
  if ($('eventModuleButtonLabel')) $('eventModuleButtonLabel').value = p.button;
  if ($('eventModuleText')) $('eventModuleText').value = p.text;
  setEventModuleDirtyState(true);
  $('eventModuleDate')?.focus?.();
}

function todayKeyRo() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(new Date());
}

function syncEventDateNote() {
  const note = $('eventModuleDateNote');
  if (!note) return;
  const d = String($('eventModuleDate')?.value || '').trim();
  if (d && d < todayKeyRo()) {
    note.textContent = '⚠️ Data e în trecut — evenimentul nu se mai afișează clienților. Alege o dată nouă.';
    note.hidden = false;
  } else if (d) {
    note.textContent = 'Evenimentul dispare singur de pe pagină după această zi.';
    note.hidden = false;
  } else {
    note.textContent = '';
    note.hidden = true;
  }
}
let hasUnsavedEventModuleChanges = false;
let eventModuleReplaceIndex = null;

export function eventFormHasUnsavedChanges() {
  return hasUnsavedEventModuleChanges === true;
}

function setEventModuleDirtyState(dirty) {
  hasUnsavedEventModuleChanges = !!dirty;
  const badge = document.getElementById('eventModuleDirtyBadge');
  if (badge) badge.style.display = hasUnsavedEventModuleChanges ? '' : 'none';
}

export function resetLocalEventImagesFromSettings() {
  const em = getCurrentSettings()?.eventModule;
  const urls = Array.isArray(em?.images) ? em.images : [];
  localEventModuleImages = urls
    .map((url) => String(url || '').trim())
    .filter(Boolean)
    .slice(0, 10)
    .map((url) => ({ type: 'remote', url }));
  eventImagesHydrated = true;
  setEventModuleDirtyState(false);
}

export function syncEventModuleFieldsVisibility() {
  const fields = $('eventModuleFields');
  if (fields) {
    fields.style.display = 'block';
  }
}

function updateEventModuleValidationHint() {
  const hint = $('eventModuleValidationHint');
  if (!hint) return;
  const en = getCurrentSettings()?.eventModule?.enabled === true;
  const n = localEventModuleImages.filter((i) => i && (i.url || i.file)).length;
  if (en && n === 0) {
    hint.style.display = 'block';
    hint.textContent = 'Poți salva fără poze; invitații vor vedea titlul și textul.';
  } else {
    hint.style.display = 'none';
    hint.textContent = '';
  }
}

export function renderEventGalleryAdminList() {
  const list = $('eventModuleGalleryList');
  if (!list) return;
  list.replaceChildren();

  if (!Array.isArray(localEventModuleImages) || localEventModuleImages.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'smallnote';
    empty.textContent = 'Nicio imagine încărcată.';
    list.appendChild(empty);
    updateEventModuleValidationHint();
    return;
  }

  localEventModuleImages.forEach((item, idx) => {
    const row = document.createElement('div');
    row.className = 'client-promo-gallery-admin-item';

    const thumb = document.createElement('img');
    thumb.className = 'client-promo-gallery-admin-item__thumb';
    thumb.alt = `Eveniment ${idx + 1}`;
    thumb.src = sanitizeImageSrc(item?.url || '') || '';

    const actions = document.createElement('div');
    actions.className = 'client-promo-gallery-admin-item__actions';

    const up = document.createElement('button');
    up.type = 'button';
    up.className = 'btn-ghost client-promo-gallery-admin-item__btn';
    up.textContent = '↑';
    up.title = 'Mută mai sus';
    up.setAttribute('aria-label', 'Mută imaginea mai sus');
    up.disabled = idx === 0;
    up.addEventListener('click', () => {
      if (idx <= 0) return;
      const tmp = localEventModuleImages[idx - 1];
      localEventModuleImages[idx - 1] = localEventModuleImages[idx];
      localEventModuleImages[idx] = tmp;
      setEventModuleDirtyState(true);
      renderEventGalleryAdminList();
    });

    const down = document.createElement('button');
    down.type = 'button';
    down.className = 'btn-ghost client-promo-gallery-admin-item__btn';
    down.textContent = '↓';
    down.title = 'Mută mai jos';
    down.setAttribute('aria-label', 'Mută imaginea mai jos');
    down.disabled = idx >= localEventModuleImages.length - 1;
    down.addEventListener('click', () => {
      if (idx >= localEventModuleImages.length - 1) return;
      const tmp = localEventModuleImages[idx + 1];
      localEventModuleImages[idx + 1] = localEventModuleImages[idx];
      localEventModuleImages[idx] = tmp;
      setEventModuleDirtyState(true);
      renderEventGalleryAdminList();
    });

    const replaceBtn = document.createElement('button');
    replaceBtn.type = 'button';
    replaceBtn.className = 'btn-ghost client-promo-gallery-admin-item__btn';
    replaceBtn.textContent = '⟳';
    replaceBtn.title = 'Înlocuiește imaginea';
    replaceBtn.setAttribute('aria-label', 'Înlocuiește imaginea');
    replaceBtn.addEventListener('click', () => {
      eventModuleReplaceIndex = idx;
      $('eventModuleGalleryUpload')?.click();
    });

    // Ștergere în doi pași (fără dialog): prima apăsare cere confirmarea, a doua șterge.
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn-danger client-promo-gallery-admin-item__btn client-promo-gallery-admin-item__btn--del';
    removeBtn.textContent = '🗑';
    removeBtn.title = 'Șterge imaginea';
    removeBtn.setAttribute('aria-label', 'Șterge imaginea');
    let removeConfirmTimer = null;
    removeBtn.addEventListener('click', () => {
      if (!removeBtn.classList.contains('is-confirming')) {
        removeBtn.classList.add('is-confirming');
        removeBtn.textContent = 'Sigur?';
        removeConfirmTimer = setTimeout(() => {
          removeBtn.classList.remove('is-confirming');
          removeBtn.textContent = '🗑';
        }, 3000);
        return;
      }
      clearTimeout(removeConfirmTimer);
      const removed = localEventModuleImages[idx];
      if (removed?.type === 'local' && removed.previewUrl) {
        try {
          URL.revokeObjectURL(removed.previewUrl);
        } catch (_) {
          /* ignore */
        }
      }
      localEventModuleImages.splice(idx, 1);
      setEventModuleDirtyState(true);
      renderEventGalleryAdminList();
    });

    actions.appendChild(up);
    actions.appendChild(down);
    actions.appendChild(replaceBtn);
    actions.appendChild(removeBtn);
    row.appendChild(thumb);
    row.appendChild(actions);
    list.appendChild(row);
  });
  updateEventModuleValidationHint();
}

export function bindEventModuleAdminControls() {
  ['eventModuleButtonLabel', 'eventModuleTitle', 'eventModuleText', 'eventModuleDate', 'eventModuleTime'].forEach((id) => {
    const el = $(id);
    if (el && !el.dataset.eventFieldBound) {
      el.dataset.eventFieldBound = '1';
      el.addEventListener('input', () => { setEventModuleDirtyState(true); syncEventDateNote(); });
      el.addEventListener('change', () => { setEventModuleDirtyState(true); syncEventDateNote(); });
    }
  });
  const presetsBox = $('eventModulePresets');
  if (presetsBox && !presetsBox.dataset.bound) {
    presetsBox.dataset.bound = '1';
    presetsBox.replaceChildren(
      ...EVENT_PRESETS.map((p) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ci-quick-campaign__btn';
        b.textContent = p.label;
        b.addEventListener('click', () => applyEventPreset(p.id));
        return b;
      })
    );
  }
  syncEventDateNote();

  const uploadEl = $('eventModuleGalleryUpload');
  if (!uploadEl || uploadEl.dataset.boundEventGallery === '1') return;

  const addBtn = $('eventModuleGalleryAddBtn');
  if (addBtn && !addBtn.dataset.eventAddBound) {
    addBtn.dataset.eventAddBound = '1';
    addBtn.addEventListener('click', () => {
      eventModuleReplaceIndex = null;
      uploadEl.click();
    });
  }

  uploadEl.addEventListener('change', () => {
    const files = Array.from(uploadEl.files || []);
    uploadEl.value = '';
    // Mai multe poze odată (doar la „Adaugă”, nu la „Înlocuiește”)
    if (files.length > 1 && eventModuleReplaceIndex === null) {
      let added = 0;
      for (const f of files) {
        if (localEventModuleImages.length >= 10) {
          showSnackbar('Poți avea maxim 10 imagini pentru eveniment.', 'error');
          break;
        }
        const v = validateFileUpload(f, {
          maxSize: 10 * 1024 * 1024,
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
          fieldName: 'Imagine eveniment'
        });
        if (!v.valid) { showSnackbar(v.message, 'error'); continue; }
        const pv = URL.createObjectURL(f);
        localEventModuleImages.push({ type: 'local', file: f, previewUrl: pv, url: pv });
        added += 1;
      }
      if (added) {
        setEventModuleDirtyState(true);
        renderEventGalleryAdminList();
      }
      return;
    }
    const file = files[0];
    if (!file) {
      eventModuleReplaceIndex = null;
      return;
    }
    const fileValidation = validateFileUpload(file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imagine eveniment'
    });
    if (!fileValidation.valid) {
      eventModuleReplaceIndex = null;
      showSnackbar(fileValidation.message, 'error');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const entry = { type: 'local', file, previewUrl, url: previewUrl };

    if (
      eventModuleReplaceIndex !== null &&
      eventModuleReplaceIndex >= 0 &&
      eventModuleReplaceIndex < localEventModuleImages.length
    ) {
      const old = localEventModuleImages[eventModuleReplaceIndex];
      if (old?.type === 'local' && old.previewUrl) {
        try {
          URL.revokeObjectURL(old.previewUrl);
        } catch (_) {
          /* ignore */
        }
      }
      localEventModuleImages[eventModuleReplaceIndex] = entry;
    } else {
      if (localEventModuleImages.length >= 10) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (_) {
          /* ignore */
        }
        showSnackbar('Poți avea maxim 10 imagini pentru eveniment.', 'error');
        eventModuleReplaceIndex = null;
        return;
      }
      localEventModuleImages.push(entry);
    }
    eventModuleReplaceIndex = null;
    setEventModuleDirtyState(true);
    renderEventGalleryAdminList();
  });

  uploadEl.dataset.boundEventGallery = '1';
}

export function hydrateEventModuleAdminFromSettings(settings) {
  const em = settings?.eventModule || {};
  if ($('eventModuleButtonLabel')) {
    const v = String(em.buttonLabel || '').trim();
    $('eventModuleButtonLabel').value = v || DEFAULT_EVENT_BUTTON_LABEL;
  }
  if ($('eventModuleTitle')) {
    const t = String(em.title || '').trim();
    $('eventModuleTitle').value = t || DEFAULT_EVENT_TITLE;
  }
  if ($('eventModuleText')) {
    $('eventModuleText').value = String(em.text || '').trim().slice(0, 2000);
  }
  if ($('eventModuleDate')) $('eventModuleDate').value = String(em.eventDate || '').trim();
  if ($('eventModuleTime')) $('eventModuleTime').value = String(em.eventTime || '').trim();
  resetLocalEventImagesFromSettings();
  renderEventGalleryAdminList();
  bindEventModuleAdminControls();
  syncEventModuleFieldsVisibility();
  import('./mod-client-modules.js?v=83.21')
    .then((m) => m.updateModClientModulesUI?.())
    .catch(() => {});
}

export async function saveEventModuleSettings(options = {}) {
  const { quiet = false } = options;
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }
  const uid = currentUser.uid;

  const enabled = getCurrentSettings()?.eventModule?.enabled === true;
  const buttonLabel = String($('eventModuleButtonLabel')?.value || '')
    .trim()
    .slice(0, 60) || DEFAULT_EVENT_BUTTON_LABEL;
  const title =
    String($('eventModuleTitle')?.value || '').trim().slice(0, 80) || DEFAULT_EVENT_TITLE;
  const text = String($('eventModuleText')?.value || '').trim().slice(0, 2000);

  // Doar dacă formularul n-a fost încă încărcat; o listă goală intenționată (toate pozele șterse) rămâne goală.
  if (!eventImagesHydrated || !Array.isArray(localEventModuleImages)) {
    resetLocalEventImagesFromSettings();
  }
  const eventDate = String($('eventModuleDate')?.value || '').trim();
  const eventTime = String($('eventModuleTime')?.value || '').trim();
  if (eventDate && !/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) {
    throw new Error('Data evenimentului nu e validă.');
  }
  if (localEventModuleImages.length > 10) {
    throw new Error('Poți salva maxim 10 imagini pentru eveniment.');
  }

  const images = [];
  for (const item of localEventModuleImages) {
    if (!item) continue;
    if (item.type === 'local' && item.file) {
      const validation = validateFileUpload(item.file, {
        maxSize: 10 * 1024 * 1024,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        fieldName: 'Imagine eveniment'
      });
      if (!validation.valid) {
        throw new Error(validation.message);
      }
      const uploadedUrl = await uploadFile(uid, 'event-gallery', item.file);
      if (uploadedUrl) images.push(uploadedUrl);
    } else if (item.type === 'remote' && item.url) {
      images.push(item.url);
    }
  }

  const toSave = {
    eventModule: {
      enabled,
      buttonLabel,
      title,
      text,
      images,
      eventDate,
      eventTime: /^\d{2}:\d{2}$/.test(eventTime) ? eventTime : ''
    }
  };

  await saveCampaignSettingsBackend(toSave);

  const currentSettings = getCurrentSettings();
  if (currentSettings) {
    currentSettings.eventModule = toSave.eventModule;
    setCurrentSettings(currentSettings);
  }

  localEventModuleImages.forEach((item) => {
    if (item?.type === 'local' && item.previewUrl) {
      try {
        URL.revokeObjectURL(item.previewUrl);
      } catch (_) {
        /* ignore */
      }
    }
  });
  localEventModuleImages = images.map((url) => ({ type: 'remote', url }));
  setEventModuleDirtyState(false);
  syncEventModuleFieldsVisibility();
  refreshAdminSummaries();

  const { applyClientUI } = await import('./slideshow.js?v=83.21');
  applyClientUI();

  if (!quiet) {
    showSnackbar('Setările evenimentului au fost salvate.', 'success');
  }

  setTimeout(() => {
    import('../services/settings.js?v=83.21')
      .then((m) => m.loadSettings(uid, true))
      .catch((err) => {
        logger.warn('[saveEventModuleSettings] Background reload failed:', err);
      });
  }, 3000);
}
